import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowUpRight, IconNotebook, IconX } from '@tabler/icons-react';
import { twMerge } from 'tailwind-merge';
import { getDocSlugForPath } from '@/docs/route-mapping';
import { useDocPageVisible } from '@/docs/useDocPageVisible';
import { useUserRole } from '@/hooks/auth.hooks';
import type { DocPage } from '@/docs/types';

type DocsModule = typeof import('@/docs');
type CategoriesModule = typeof import('@/docs/categories');

let docsPromise: Promise<[DocsModule, CategoriesModule]> | undefined;
const loadDocs = () => {
  docsPromise ??= Promise.all([import('@/docs'), import('@/docs/categories')]);
  return docsPromise;
};

interface UnderstandButtonProps {
  /** Documentation page to display. Resolved from the current url when omitted. */
  docSlug?: string;
  className?: string;
}

/**
 * "Comprendre" icon displayed next to page titles: opens a side panel with the
 * documentation of the whole page.
 */
export const UnderstandButton = ({
  docSlug,
  className,
}: UnderstandButtonProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const slug = docSlug ?? getDocSlugForPath(location.pathname);
  const [open, setOpen] = useState(false);
  // Hidden when the user's role doesn't give access to this documentation page
  const visible = useDocPageVisible(slug);

  if (!slug || !visible) return null;

  return (
    <>
      <button
        type="button"
        title={t('common:understandThisPage')}
        aria-label={t('common:understandThisPage')}
        className={twMerge(
          'inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-purple-5433FF text-white shadow-[0_4px_12px_rgba(84,51,255,0.35)] transition-transform hover:-translate-y-[1px] hover:shadow-[0_6px_16px_rgba(84,51,255,0.45)]',
          className,
        )}
        onClick={() => setOpen(true)}
      >
        <IconNotebook size={18} stroke={1.8} />
      </button>
      {open && <HelpDrawer slug={slug} onClose={() => setOpen(false)} />}
    </>
  );
};

interface HelpDrawerProps {
  slug: string;
  onClose: () => void;
}

const HelpDrawer = ({ slug, onClose }: HelpDrawerProps) => {
  const { t } = useTranslation();
  const { hasNoRole } = useUserRole();
  const [page, setPage] = useState<DocPage | null>();
  const [categoryTitle, setCategoryTitle] = useState<string>();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadDocs().then(([docs, categories]) => {
      if (cancelled) return;
      const found = docs.getDocPage(slug) ?? null;
      setPage(found);
      setCategoryTitle(
        found ? categories.getDocCategory(found.category)?.title : undefined,
      );
    });
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [slug]);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const Content = page?.Content;

  return createPortal(
    <div className="fixed inset-0 z-[1050]" role="dialog" aria-modal="true">
      <div
        className={twMerge(
          'absolute inset-0 bg-black-071336/30 transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />
      <aside
        className={twMerge(
          'absolute right-0 top-0 flex h-full w-full max-w-[620px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-out dark-mode:bg-coal-600',
          visible ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex items-start gap-3 border-b border-gray-100 px-6 py-5 dark-mode:border-gray-300">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-5433FF text-white">
            <IconNotebook size={22} stroke={1.8} />
          </span>
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-xs font-medium uppercase tracking-wide text-purple-5433FF">
              {t('common:understand')}
              {categoryTitle ? ` · ${categoryTitle}` : ''}
            </span>
            <h2 className="text-lg font-semibold text-black-071336 dark-mode:text-gray-900">
              {page?.title ?? '…'}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('common:close')}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <IconX size={20} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {page === undefined && (
            <div className="flex justify-center py-10">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-purple-5433FF border-t-transparent" />
            </div>
          )}
          {page === null && (
            <p className="text-sm text-gray-600">
              {t('common:documentationPageNotFound')}
            </p>
          )}
          {page && Content && (
            <div className="flex flex-col gap-6">
              <p className="rounded-xl bg-purple-F7F5FF px-4 py-3 text-sm leading-relaxed text-gray-800 dark-mode:bg-coal-500">
                {page.summary}
              </p>
              <Content />
            </div>
          )}
        </div>
        {/* No access to the documentation page without a role */}
        {!hasNoRole && (
          <footer className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark-mode:border-gray-300">
            <Link
              to="/documentation"
              className="text-sm font-medium text-gray-600 hover:text-purple-5433FF"
              onClick={onClose}
            >
              {t('common:allDocumentation')}
            </Link>
            <Link
              to={`/documentation/${slug}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-5433FF px-3.5 py-2 text-sm font-medium text-white shadow-md hover:bg-purple-5433FF/90"
              onClick={onClose}
            >
              {t('common:openInDocumentation')}
              <IconArrowUpRight size={16} />
            </Link>
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
};
